import { useEffect, useState, useCallback } from 'react';

import { GLOBAL } from 'saddlebag-browser';
import { v4 as uuid } from 'uuid';

import {
  buildComboRecommendationImpressionEvent,
  buildComboRecommendationInteractionEvent,
} from './utils/buildEvent';
import sendEvent from './utils/sendEvent';

import type { ComboRecommendationTrackingConfig } from './types';

const $window = GLOBAL.getWindow();

/**
 * Hook for tracking combo recommendation MBD events (impression + interaction)
 *
 * @param {ComboRecommendationTrackingConfig} baseConfig - The base configuration for tracking
 * @returns {object} An object with ref and trackClick functions
 */
const useComboRecommendationTracking = (
  baseConfig: ComboRecommendationTrackingConfig,
) => {
  const [elementRef, setElementRef] = useState<HTMLElement | null>(null);
  const [impressionGuid, setImpressionGuid] = useState<string | null>(null);

  // Track impression when element is mounted
  useEffect(() => {
    if (!elementRef) return;

    const guid = uuid();
    setImpressionGuid(guid);

    sendEvent(
      buildComboRecommendationImpressionEvent(
        $window.__internal.screenViewEventGuid,
        baseConfig,
      ),
      guid,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementRef]);

  // Function to track individual clicks with additional context
  const trackClick = useCallback(
    (additionalProperties?: {
      clicked_element?: string;
      redirect_url?: string;
      vertical?: string;
    }) => {
      if (!impressionGuid) return;

      const config: ComboRecommendationTrackingConfig = {
        ...baseConfig,
        stringProperties: {
          ...baseConfig.stringProperties,
          ...additionalProperties,
        },
      };

      sendEvent(
        buildComboRecommendationInteractionEvent(impressionGuid, config),
      );
    },
    [impressionGuid, baseConfig],
  );

  return {
    ref: setElementRef,
    trackClick,
  };
};

export default useComboRecommendationTracking;
